"use client";
import './i18n/autoTranslate';

import React, { useState } from 'react';
import { 
  MessageSquare, Mail, Users, Briefcase, 
  Shield, Clock, CheckCircle, Send,
  ArrowRight, Zap, Headphones,
  HelpCircle, TrendingUp, Building, Sparkles,
  FileText, Target, Award
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Globe from '../components/lightswind/globe';
import SEO from './SEO';

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:5000";

// Animation Components
const SectionWrapper = ({ children, className = "" }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      viewport={{ once: true }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const GlassCard = ({ children, className = "", hover = true }) => {
  return (
    <motion.div
      whileHover={hover ? { y: -4, transition: { duration: 0.3 } } : {}}
      className={`
        backdrop-blur-xl 
        bg-white/5 
        border border-white/10 
        rounded-2xl
        shadow-[0_0_30px_rgba(108,99,255,0.05)]
        ${hover ? 'hover:bg-white/8 hover:border-white/20 hover:shadow-[0_0_50px_rgba(108,99,255,0.1)]' : ''}
        transition-all duration-300
        ${className}
      `}
    >
      <img src={"/assets/images/Gemini_Generated_6_dlmovh.png"} className="absolute -inset-0 object-cover w-full -z-10 h-full rounded-3xl opacity-[0.09]" alt="" />
      
      {children}
    </motion.div>
  );
};

const StaggerContainer = ({ children, className = "" }) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2
          }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const StaggerItem = ({ children, index = 0, className = "" }) => {
  const direction = index % 2 === 0 ? -100 : 100;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: direction, y: 100 }}
      whileInView={{opacity: 1, x: 0, y: 0}}
      transition= {{
        duration: 2,
        ease: [0.22, 1, 0.36, 1]
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    topic: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);


const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);

  try {
    const res = await fetch(`${API_BASE}/api/contact/inquiry`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        full_name: formData.name,
        email: formData.email,
        inquiry_type: formData.topic || "other",
        message: formData.message,
        page_url: window.location.href,
      }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      // backend returns {error: "..."}
      throw new Error(data?.error || "Failed to submit inquiry");
    }

    setIsSubmitted(true);
    alert("Your message has been sent successfully! Our team will get back to you within 24-48 hours.");
    setFormData({ name: "", email: "", topic: "", message: "" });
  } catch (err) {
    console.error(err);
    alert(err?.message || "Something went wrong");
  } finally {
    setIsSubmitting(false);
  }
};

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="relative min-h-screen bg-[#07070A] text-white overflow-hidden">
      <SEO
        title="Contact InterviewGenieAI — AI Interview Preparation Support"
        description="Contact InterviewGenieAI for support, questions, feedback, partnerships, or help with your AI interview preparation account."
        canonical="https://interviewgenieai.com/contact"
      />
      <div className='w-full  opacity-[0.2]  relative'>
        <Globe
          size='full'
          theta={0.2}
          dark={1}
          scale={1.1}
          diffuse={1.5}
          baseColor="#1a1a1a"
          markerColor="#6C63FF"
          glowColor="#444444"
          className="absolute"
        />
      </div>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#6C63FF]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#8B7CFF]/5 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-3 mb-8"
            >
              <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
              <span className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase">
                Connect With Us
              </span>
              <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6"
            >
              <span className="bg-gradient-to-r from-white via-white/90 to-white bg-clip-text text-transparent">
                Contact
              </span>
              <br />
              <span className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] bg-clip-text text-transparent">
                InterviewGenieAI
              </span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed"
            >
              Whether you need support, want to explore partnerships, or have feedback, our team is here to help you succeed.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Contact Reasons Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionWrapper className="mb-12">
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-4">
                How Can We Assist You?
              </h2>
              <p className="text-lg text-gray-300 max-w-xl mx-auto leading-relaxed">
                Our dedicated teams are ready to support your interview preparation journey in multiple ways.
              </p>
            </div>
          </SectionWrapper>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              {
                icon: HelpCircle,
                title: "Product Support",
                description: "Technical questions, feature guidance, platform assistance",
                color: "from-[#6C63FF]/10 to-transparent"
              },
              {
                icon: Users,
                title: "Coaching & Mentorship",
                description: "Career strategy, interview coaching, professional development",
                color: "from-[#6C63FF]/10 to-transparent"
              },
              {
                icon: Briefcase,
                title: "Partnerships",
                description: "Corporate programs, university partnerships, integrations",
                color: "from-[#6C63FF]/10 to-transparent"
              },
              {
                icon: Building,
                title: "Enterprise Access",
                description: "Team licenses, custom solutions, organizational deployment",
                color: "from-[#6C63FF]/10 to-transparent"
              },
              {
                icon: MessageSquare,
                title: "Feedback",
                description: "Feature suggestions, platform improvements, user experience",
                color: "from-[#6C63FF]/10 to-transparent"
              }
            ].map((reason, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="p-6 h-full">
                  <div className="flex flex-col items-center text-center">
                    <div className={`w-14 h-14 rounded-xl border border-white/20 bg-gradient-to-br ${reason.color} flex items-center justify-center mb-4`}>
                      <reason.icon className="w-7 h-7 text-[#6C63FF]" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-3">
                      {reason.title}
                    </h3>
                    <p className="text-sm text-gray-400">
                      {reason.description}
                    </p>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent via-[#6C63FF]/5 to-transparent">
        <div className="max-w-4xl mx-auto">
          <SectionWrapper>
            <GlassCard className="p-8 md:p-12">
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-3 mb-4">
                  <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                  <span className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase">
                    Send a Message
                  </span>
                  <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-4">
                  Get in Touch Directly
                </h2>
                <p className="text-gray-300 max-w-lg mx-auto">
                  Fill out the form below and our dedicated team will respond promptly to your inquiry.
                </p>
              </div>

              {isSubmitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-24 h-24 rounded-full bg-gradient-to-r from-[#6C63FF]/20 to-[#8B7CFF]/20 flex items-center justify-center mx-auto mb-8">
                    <CheckCircle className="w-12 h-12 text-[#6C63FF]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">Message Received</h3>
                  <p className="text-gray-300 mb-8 max-w-md mx-auto leading-relaxed">
                    Thank you for reaching out to InterviewGenieAI. We've received your message and our team will respond within 24-48 hours.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="px-6 py-3 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6C63FF] focus:ring-2 focus:ring-[#6C63FF]/20 transition-all duration-300"
                        placeholder="Enter your full name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6C63FF] focus:ring-2 focus:ring-[#6C63FF]/20 transition-all duration-300"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="topic" className="block text-sm font-medium text-gray-300">
                      Inquiry Type *
                    </label>
                    <select
                      id="topic"
                      name="topic"
                      value={formData.topic}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#6C63FF] focus:ring-2 focus:ring-[#6C63FF]/20 transition-all duration-300 appearance-none"
                    >
                      <option value="" disabled className="bg-[#07070A]">Select inquiry type</option>
                      <option value="support" className="bg-[#07070A]">Product Support</option>
                      <option value="coaching" className="bg-[#07070A]">Career Coaching Inquiry</option>
                      <option value="partnership" className="bg-[#07070A]">Partnership Opportunity</option>
                      <option value="enterprise" className="bg-[#07070A]">Enterprise Access Request</option>
                      <option value="feedback" className="bg-[#07070A]">Feedback & Suggestions</option>
                      <option value="technical" className="bg-[#07070A]">Technical Question</option>
                      <option value="other" className="bg-[#07070A]">Other Inquiry</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300">
                      Your Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6C63FF] focus:ring-2 focus:ring-[#6C63FF]/20 transition-all duration-300 resize-none"
                      placeholder="Please provide details about your inquiry..."
                    />
                  </div>
                  
                  <div className="pt-4">
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      // onClick={() => navigate("/pricing")}
                      type="submit"
                      disabled={isSubmitting}
                      style={{
                        boxShadow: 'inset 0 -4px 10px rgba(255, 255, 255, 0.3), 0 0 40px rgba(108, 99, 255, 0.3)'
                      }}
                      className="w-full px-8 py-2 group rounded-xl bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] text-white font-medium transition-all duration-300 hover:shadow-[0_0_50px_rgba(108,99,255,0.4)] flex items-center justify-center gap-3 relative overflow-hidden"
                    >
                      <div 
                        className="absolute -bottom-1 left-[20%] w-1/2 right-0 h-4 rounded-full bg-white/70 blur-[10px] pointer-events-none"
                      />
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending Message...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                          Send Message to Our Team
                        </>
                      )}
                    </motion.button>

                  </div>
                </form>
              )}
            </GlassCard>
          </SectionWrapper>
        </div>
      </section>

      {/* Alternative Contact Info */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionWrapper>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-4">
                Direct Contact Channels
              </h2>
              <p className="text-lg text-gray-300 max-w-xl mx-auto">
                Prefer to reach out directly? Here are our primary contact methods.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <GlassCard className="p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-2xl border border-white/20 bg-gradient-to-br from-[#6C63FF]/10 to-transparent flex items-center justify-center mb-6">
                    <Mail className="w-8 h-8 text-[#6C63FF]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Support Email</h3>
                  <a 
                    href="mailto:support@interviewgenie.ai" 
                    className="text-lg font-medium text-[#6C63FF] hover:text-[#8B7CFF] transition-colors mb-4"
                  >
                    support@interviewgenie.ai
                  </a>
                  <p className="text-sm text-gray-400">
                    For technical support, platform questions, and general assistance
                  </p>
                </div>
              </GlassCard>

              <GlassCard className="p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-2xl border border-white/20 bg-gradient-to-br from-[#6C63FF]/10 to-transparent flex items-center justify-center mb-6">
                    <Briefcase className="w-8 h-8 text-[#6C63FF]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Business Inquiries</h3>
                  <a 
                    href="mailto:partnerships@interviewgenie.ai" 
                    className="text-lg font-medium text-[#6C63FF] hover:text-[#8B7CFF] transition-colors mb-4"
                  >
                    partnerships@interviewgenie.ai
                  </a>
                  <p className="text-sm text-gray-400">
                    For partnerships, corporate access, and enterprise solutions
                  </p>
                </div>
              </GlassCard>

              <GlassCard className="p-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-2xl border border-white/20 bg-gradient-to-br from-[#6C63FF]/10 to-transparent flex items-center justify-center mb-6">
                    <Clock className="w-8 h-8 text-[#6C63FF]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">Response Commitment</h3>
                  <div className="text-2xl font-bold text-white mb-3">24-48 Hours</div>
                  <p className="text-sm text-gray-400">
                    We respond to all professional inquiries within 48 hours, typically much sooner
                  </p>
                </div>
              </GlassCard>
            </div>
          </SectionWrapper>
        </div>
      </section>

      {/* Trust & Privacy Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent via-[#8B7CFF]/5 to-transparent">
        <div className="max-w-4xl mx-auto">
          <SectionWrapper>
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-4">
                Enterprise Security & Privacy
              </h2>
              <p className="text-gray-300 max-w-xl mx-auto">
                Your communication is protected with enterprise-grade security measures.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <GlassCard className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl border border-white/20 bg-gradient-to-br from-[#6C63FF]/10 to-transparent flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-[#6C63FF]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Enterprise Security</h3>
                    <p className="text-gray-400">
                      All communications are encrypted with industry-standard protocols. Your data is protected with multiple layers of security.
                    </p>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl border border-white/20 bg-gradient-to-br from-[#6C63FF]/10 to-transparent flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-[#6C63FF]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Privacy Protection</h3>
                    <p className="text-gray-400">
                      Your information is handled with strict confidentiality and is never shared with third parties without explicit consent.
                    </p>
                  </div>
                </div>
              </GlassCard>
            </div>
          </SectionWrapper>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <SectionWrapper>
            <GlassCard className="p-12 text-center">
              <div className="relative">
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#6C63FF]/5 rounded-full blur-3xl" />
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#8B7CFF]/5 rounded-full blur-3xl" />
                
                <div className="relative z-10">
                  <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-6">
                    Ready to Transform Your Interview Skills?
                  </h2>
                  <p className="text-lg text-gray-300 mb-10 max-w-xl mx-auto leading-relaxed">
                    While you await our response, explore our AI-powered platform and start practicing with confidence.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-6 justify-center">
                    <Link 
                      to="/pricing"
                      className="px-8 py-4 rounded-xl bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] text-white font-semibold hover:shadow-[0_0_60px_rgba(108,99,255,0.3)] transition-all duration-300 flex items-center justify-center gap-3 group"
                    >
                      <Zap className="w-5 h-5" />
                      Start Free Trial
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>

                  </div>
                </div>
              </div>
            </GlassCard>
          </SectionWrapper>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-4 sm:px-6 lg:px-8 bg-black/30 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <StaggerContainer className="grid md:grid-cols-4 gap-8 mb-12">
            <StaggerItem index={0}>
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <img src={"/GenieAI_2.png"} className="text-white" />
                  </div>
                  <span className="font-bold text-white">InterviewGenieAI</span>
                </div>
                <p className="text-gray-400 text-sm">The most advanced AI interview preparation platform</p>
              </div>
            </StaggerItem>
            
            {[
              { title: "Product", items: ["Features", "How It Works", "Pricing", "Demo"] },
              { title: "Resources", items: ["Blog", "Tutorials", "Documentation", "Support"] },
              { title: "Company", items: ["About", "Careers", "Contact", "Legal"] }
            ].map((section, index) => (
              <StaggerItem key={index} index={index + 1}>
                <div>
                  <h4 className="font-bold mb-4 text-white">{section.title}</h4>
                  <ul className="space-y-2 text-sm">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex}>
                        <a href="#" className="text-gray-400 hover:text-[#6C63FF] transition-colors">
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center"
          >
            <p className="text-gray-400 text-sm">© 2025 InterviewGenieAI. All rights reserved.</p>
            <div className="flex items-center gap-6 mt-4 md:mt-0">
              {["Terms", "Privacy", "Cookies"].map((item, index) => (
                <a key={index} href="#" className="text-gray-400 hover:text-[#6C63FF] transition-colors text-sm">
                  {item}
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </footer>
    </div>
  );
};

export default ContactUs;
